/** Wallet balance from GET /billing/balance and org balance endpoints. */
export type WalletBalance = {
    balance?: number
    balance_usd?: number
    currency?: string
    display?: {
        amount: number
        currency: string
        fx_rate_inr_per_usd?: number
        fx_as_of?: string | null
        fx_source?: string | null
        alternate?: { amount: number; currency: string }
    }
    org_id?: string
    user_id?: string
    error?: string
}

export function walletPrimaryAmount(balance: WalletBalance | null | undefined): number {
    if (!balance) return 0
    if (balance.balance != null) return Number(balance.balance)
    return Number(balance.balance_usd ?? 0)
}

export function formatMoney(
    amount: number,
    currency: string,
    options?: { decimals?: number; compact?: boolean },
): string {
    const decimals = options?.decimals ?? 2
    const value = Number.isFinite(amount) ? amount : 0

    if (currency === 'INR') {
        const formatted = value.toLocaleString('en-IN', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        })
        return `₹${formatted}`
    }

    if (currency === 'USD') {
        const formatted = value.toLocaleString('en-US', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        })
        return `$${formatted}`
    }

    return `${currency} ${value.toFixed(decimals)}`
}

export function formatWalletBalance(
    balance: WalletBalance | null | undefined,
    options?: { decimals?: number },
): string {
    if (!balance) return '—'
    const currency = balance.currency ?? 'USD'
    return formatMoney(walletPrimaryAmount(balance), currency, options)
}

export function formatWalletBalanceCompact(balance: WalletBalance | null | undefined): string {
    return formatWalletBalance(balance, { decimals: 2 })
}

/** Maps prices are stored in USD. INR wallets see the rupee amount at the billing rate. */
export function formatUsdForWallet(
    amountUsd: number,
    balance: WalletBalance | null | undefined,
    options?: { decimals?: number },
): string {
    const currency = (balance?.currency || 'USD').toUpperCase()
    const rate = balance?.display?.fx_rate_inr_per_usd
    if (currency === 'INR' && typeof rate === 'number' && rate > 0) {
        return formatMoney(amountUsd * rate, 'INR', options)
    }
    return formatMoney(amountUsd, 'USD', options)
}
